import { motion } from "framer-motion";
import { useState } from "react";
import { MessageSquareCode, Wrench, Brain } from "lucide-react";

const CARDS = [
  {
    id: "prompt",
    label: "Prompt",
    icon: MessageSquareCode,
    tag: "01 / instructions",
    tagline: "The intent-shaping layer.",
    description:
      "System + user prompts that steer the model with context, tone, constraints, and few-shot examples.",
    code: `system: |
  You are a Support Triage agent for CareStack.
  Classify tickets into: [billing, clinical, tech].
  Always cite the exact policy paragraph.

user: "{{ticket_body}}"`,
  },
  {
    id: "tools",
    label: "Tools",
    icon: Wrench,
    tag: "02 / capabilities",
    tagline: "The action layer.",
    description:
      "Function calls, retrieval, integrations. The concrete abilities your skill can invoke to get work done.",
    code: `tools:
  - name: search_kb
    args: { query: str, top_k: int }
  - name: create_ticket
    args: { subject: str, body: str }
  - name: notify_slack
    args: { channel: str, text: str }`,
  },
  {
    id: "logic",
    label: "Logic",
    icon: Brain,
    tag: "03 / control flow",
    tagline: "The reasoning layer.",
    description:
      "State, routing, guards, validators. The deterministic glue that makes your skill safe and reliable.",
    code: `if intent == "billing":
  ticket = search_kb(query=body, top_k=3)
  if confidence(ticket) < 0.6:
      route_to("human")
  else:
      draft = compose_reply(ticket)
      notify_slack("#care-ops", draft)`,
  },
];

export default function WhatIsSkill() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="skill"
      className="relative py-16 lg:py-20 px-6 md:px-12 lg:px-24"
      data-testid="skill-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C] mb-4">
            // 01 · Definition
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
            What <span className="italic font-normal text-zinc-400">is</span>{" "}
            an <span className="text-gradient-neon">AI Skill</span>?
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
            A packaged AI capability:{" "}
            <span className="text-white font-medium">
              a prompt + tools + logic
            </span>{" "}
            that solves a real problem for a team or developer internally.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            const isActive = active === card.id;
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                onMouseEnter={() => setActive(card.id)}
                onMouseLeave={() => setActive(null)}
                data-testid={`skill-card-${card.id}`}
                className={`group relative rounded-2xl glass overflow-hidden transition-all duration-500 cursor-pointer ${
                  isActive
                    ? "border-[#2FCC7C]/60 shadow-[0_0_40px_rgba(47,204,124,0.25)] -translate-y-1"
                    : "hover:border-[#2FCC7C]/30"
                }`}
              >
                {/* top strip */}
                <div className="flex items-center justify-between px-5 pt-5">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${
                        isActive
                          ? "border-[#2FCC7C] bg-[#2FCC7C]/10"
                          : "border-white/10 bg-white/[0.02]"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 transition-colors ${
                          isActive ? "text-[#2FCC7C]" : "text-zinc-400"
                        }`}
                      />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {card.tag}
                    </span>
                  </div>
                  <span
                    className={`w-2 h-2 rounded-full transition-all ${
                      isActive ? "bg-[#2FCC7C] shadow-[0_0_12px_#2FCC7C]" : "bg-zinc-700"
                    }`}
                  />
                </div>

                <div className="px-5 pt-4">
                  <h3 className="font-heading text-3xl font-bold">{card.label}</h3>
                  <p className="mt-1 text-sm text-zinc-500">{card.tagline}</p>
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed min-h-[64px]">
                    {card.description}
                  </p>
                </div>

                {/* code snippet - expands on hover */}
                <div
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    isActive ? "max-h-96 opacity-100" : "max-h-24 opacity-70"
                  }`}
                >
                  <div className="mx-5 mb-5 mt-4 rounded-xl bg-[#020202] border border-white/[0.06] overflow-hidden">
                    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.06]">
                      <span className="w-2 h-2 rounded-full bg-red-500/50" />
                      <span className="w-2 h-2 rounded-full bg-yellow-500/50" />
                      <span className="w-2 h-2 rounded-full bg-[#2FCC7C]/70" />
                      <span className="ml-2 font-mono text-[10px] text-zinc-500">
                        skill.{card.id}.yml
                      </span>
                    </div>
                    <pre className="font-mono text-[11px] leading-relaxed text-zinc-300 p-4 overflow-x-auto">
                      <code>{card.code}</code>
                    </pre>
                  </div>
                </div>

                {/* accent line */}
                <div
                  className={`absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-[#2FCC7C] to-[#2D7BF0] transition-all duration-700 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
